import React, { useEffect, useRef, useState } from 'react';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

// Full-screen signature capture (spec §1.1 — the ONLY signature entry
// point). Pointer/touch drawing on a canvas, exported as a PNG data URL.
// The caller persists the data URL to engineer_signatures.signature_data.
export default function SignaturePad({ clientName = 'Client', onConfirm, onCancel }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Match the canvas backing store to its CSS size (devicePixelRatio for
    // crisp output — signatures are audit records).
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#17211f';
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    drawing.current = true;
    setHasInk(true);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  // Export the signature as a downscaled PNG data URL. The capture canvas is
  // full-screen at devicePixelRatio (often 1000-2400px wide); storing that
  // raw would blow past engineer_signatures.signature_data's cap (100k
  // chars after migration 018). Signatures stay readable at 600px wide and
  // compress to ~5-25KB — an audit record, not a photo.
  const exportSignature = () => {
    const canvas = canvasRef.current;
    const MAX_W = 600;
    if (canvas.width <= MAX_W) return canvas.toDataURL('image/png');
    const scale = MAX_W / canvas.width;
    const out = document.createElement('canvas');
    out.width = MAX_W;
    out.height = Math.max(1, Math.round(canvas.height * scale));
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/png');
  };

  const confirm = async () => {
    if (!hasInk) {
      setError('Please capture a signature before confirming.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const dataUrl = exportSignature();
      await onConfirm(dataUrl);
    } catch (err) {
      setError(err?.message || 'Could not save the signature. Please try again.');
      setIsSaving(false);
    }
  };

  return (
    <div className="signature-overlay" role="dialog" aria-modal="true" aria-label="Capture signature">
      <div className="signature-pad">
        <h2>Capture Client Signature</h2>
        <p className="sig-client">Client: <strong>{clientName}</strong></p>

        <canvas
          ref={canvasRef}
          className="sig-canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />

        {error && <p className="form-hint" style={{ color: '#b91c1c' }}>{error}</p>}

        <div className="sig-actions">
          <div className="sig-left">
            <button className="secondary-button" onClick={clear} disabled={!hasInk || isSaving}>
              <SafeIcon icon={Icons.RefreshCw} size={14} />
              Clear
            </button>
            <button className="secondary-button" onClick={onCancel} disabled={isSaving}>
              Cancel
            </button>
          </div>
          <button
            className="primary-button"
            onClick={confirm}
            disabled={!hasInk || isSaving}
            aria-label="Confirm signature"
          >
            <SafeIcon icon={Icons.Check} size={16} />
            {isSaving ? 'Saving…' : 'Confirm & Complete Job'}
          </button>
        </div>

        <p className="sig-hint">
          By signing, the client confirms the work described has been completed to their satisfaction.
        </p>
      </div>
    </div>
  );
}
